<template>
  <nav
    ref="navRoot"
    class="side-nav"
    :class="{ 'is-hidden': isHidden }"
    @mouseenter="onPointerEnter"
    @mouseleave="onPointerLeave"
    @focusin="onFocusIn"
    @focusout="onFocusOut"
  >
    <ul ref="navList" class="side-nav__list">
      <!-- Capture, not bubble: RouterLink binds its own click on this element
           and it runs first, so a bubble-phase preventDefault arrived after the
           router had already navigated and jumped to the hash, leaving the
           smooth scroll nothing left to travel. -->
      <li v-for="item in navItems" :key="item.id" class="side-nav__item">
        <NuxtLink
          :to="item.path"
          class="side-nav__pill"
          :class="{ 'is-active': isActive(item) }"
          :aria-current="isActive(item) ? 'true' : undefined"
          @click.capture="(e) => handleNavClick(e, item.path)"
        >
          <span class="side-nav__icon">
            <svg v-if="item.id === 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M3 11l9-8 9 8"/>
              <path d="M5 10v10a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1v-5h2v5a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1V10"/>
            </svg>
            <svg v-else-if="item.id === 2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="3" width="7" height="7"/>
              <rect x="14" y="3" width="7" height="7"/>
              <rect x="14" y="14" width="7" height="7"/>
              <rect x="3" y="14" width="7" height="7"/>
            </svg>
            <svg v-else-if="item.id === 4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="2" y="4" width="20" height="16" rx="2"/>
              <polyline points="3 6 12 13 21 6"/>
            </svg>
            <svg v-else-if="item.id === 6" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3z"/>
              <path d="M18.5 15.5l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8.8-2.2z"/>
            </svg>
            <svg v-else-if="item.id === 7" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 20h9"/>
              <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/>
            </svg>
            <svg v-else-if="item.id === 5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M9 2v6.3L4.2 17a2 2 0 0 0 1.8 3h12a2 2 0 0 0 1.8-3L15 8.3V2"/>
              <line x1="8.5" y1="2" x2="15.5" y2="2"/>
              <line x1="7" y1="14" x2="17" y2="14"/>
            </svg>
          </span>
        </NuxtLink>
        <span class="side-nav__label">{{ item.name }}</span>
      </li>
    </ul>
  </nav>
</template>

<script setup>
import { onMounted, onUnmounted, ref } from 'vue';
import { gsap } from 'gsap';

// Navigation data
// Every item but the last maps to a section that lives on the home page's
// own horizontal track; "Explore" is the one deliberate exception,
// jumping to the separate /explore page instead of a "/#" section.
// Order matches the order of the panels on the home track, so the nav reads
// as a map of the page. "About" is intentionally absent — it lives in the
// hero alongside Home, so a separate entry pointed at the same panel.
const navItems = [
  { id: 1, name: 'Home', path: '/' },
  { id: 2, name: 'Projects', path: '/#projects' },
  { id: 6, name: 'AI', path: '/#ai' },
  { id: 7, name: 'Writing', path: '/#blog' },
  { id: 4, name: 'Contact', path: '/#contact' },
  { id: 5, name: 'Explore', path: '/explore' },
];

// Handle navigation click — hijacks to the horizontal track when active,
// otherwise falls through to normal NuxtLink navigation. Clicking "Home"
// while already on the home page scrolls back to the top instead.
const { navigateToPanel } = usePanelNav();
const route = useRoute();

// Highlights whichever panel is currently on screen. The composable publishes
// the visible panel's id as you scroll; each nav entry claims the section its
// hash points at, and "Home" claims the hero.
const scrollState = useState('horizontalScroll');

function isActive(item) {
  if (item.path === '/explore') return route.path === '/explore';

  const hash = item.path.includes('#') ? item.path.split('#')[1] : null;
  if (route.path !== '/') return false;

  const active = scrollState.value?.activeSection;
  return hash ? active === hash : active === 'hero';
}

function handleNavClick(event, path) {
  // A tapped pill keeps focus and, on a touch screen, keeps :hover as well.
  // Both are reasons the dock would consider itself in use immediately after
  // being used. Letting go here is the difference between it tidying itself
  // away and sitting over the section it just took you to.
  if (autoHideQuery?.matches) {
    event.currentTarget?.blur?.();
    scheduleHide();
  }

  if (path === '/') {
    if (route.path === '/') {
      event.preventDefault();
      useLenis()?.scrollTo(0, { duration: 1.2 });
      return;
    }

    // Home means the hero. Arriving from a nested page the track would
    // otherwise restore the panel that page was opened from, because that is
    // the position still on record. The back link is what returns you there.
    try {
      sessionStorage.removeItem('h-scroll-position:/');
      sessionStorage.removeItem('h-scroll-x:/');
    } catch {
      // private mode / storage disabled
    }
  }

  if (path) {
    navigateToPanel(event, path);
  }
}

// Auto-hide, mobile only. There the nav floats over a narrow column and only
// earns its space while the user is travelling: scrolling means "going
// somewhere", a pause means they've arrived and are reading, at which point the
// dock is just something sitting on top of the content.
//
// Desktop keeps it permanently visible — the panels are wide enough that it
// never covers anything, and on a pointer device a dock that vanishes is more
// startling than useful.
//
// Hidden means opacity 0 *and* pointer-events: none — an invisible element
// that still swallowed clicks would be exactly the obstruction this removes.
// The consequence is that the mouse alone cannot summon it back (no hover
// target to enter), which is fine: any scroll brings it straight back, and
// that is the gesture the behaviour is built around.
const HIDE_DELAY = 1400; // long enough to reach for a pill after stopping
const INTRO_HOLD = 2400; // stays up past the mount animation so it's noticed
const isHidden = ref(false);
const navRoot = ref(null);

let hideTimer = null;
let scrollRaf = null;
let autoHideQuery = null;
let hoverQuery = null;

// Asked at the moment of hiding rather than tracked in flags.
//
// Flags were the bug. A tap on a phone produces a compatibility mouseenter,
// and the matching mouseleave frequently never arrives, because a finger that
// has lifted leaves the element "hovered" until something else is touched. The
// flag stayed set, the timer woke, saw it, rescheduled, and the dock never
// hid again for the rest of the visit.
//
// Reading the DOM cannot get stuck: whatever events did or did not fire, this
// is the truth at the instant it matters.
function holdsOpen() {
  const el = navRoot.value;
  if (!el) return false;

  // Only where hovering is a real state. On a touch screen :hover sticks to
  // whatever was tapped last, which is not somebody reaching for the dock.
  if (hoverQuery?.matches && el.matches(':hover')) return true;

  // Keyboard focus only. Tapping a pill focuses it too, and that must not
  // count, or the dock stays up exactly when it has just been used.
  const active = document.activeElement;
  return Boolean(active) && el.contains(active) && Boolean(active.matches?.(':focus-visible'));
}

function scheduleHide(delay = HIDE_DELAY) {
  clearTimeout(hideTimer);

  // The single gate for the whole behaviour: with this false nothing ever
  // hides, so the pointer and focus handlers below need no guard of their own.
  if (!autoHideQuery?.matches) return;

  hideTimer = setTimeout(() => {
    // Never pull it out from under a cursor that's already on it, or out of
    // keyboard focus. Re-arm instead and re-check later.
    if (holdsOpen()) {
      scheduleHide();
      return;
    }
    isHidden.value = true;
  }, delay);
}

function reveal() {
  isHidden.value = false;
  scheduleHide();
}

// Throttled to one call per frame. Scroll fires far more often than that, and
// every call churns a timer.
function handleScroll() {
  if (scrollRaf) return;
  scrollRaf = requestAnimationFrame(() => {
    scrollRaf = null;
    reveal();
  });
}

// `wheel` as well as `scroll`: at either end of the track the wheel keeps
// turning while the scroll position is clamped, so no scroll event fires —
// but the user is still very much interacting.
function handleWheel() {
  reveal();
}

// These four only ever show it and re-arm the timer. Whether it may hide is
// decided by holdsOpen() when the timer fires, so nothing here can leave the
// dock stuck open by failing to fire its counterpart.
function onPointerEnter() {
  if (!hoverQuery?.matches) return;
  clearTimeout(hideTimer);
  isHidden.value = false;
}

function onPointerLeave() {
  scheduleHide();
}

function onFocusIn() {
  clearTimeout(hideTimer);
  isHidden.value = false;
  // Re-armed even so: if this was a tap rather than the keyboard, holdsOpen()
  // will say so and it will hide on schedule.
  scheduleHide();
}

function onFocusOut() {
  scheduleHide();
}

// Listeners are attached only in the mode that uses them. Desktop is where the
// horizontal-scroll pin runs, so it doesn't get scroll handlers it has no use
// for.
function addAutoHideListeners() {
  window.addEventListener('scroll', handleScroll, { passive: true });
  window.addEventListener('wheel', handleWheel, { passive: true });
  window.addEventListener('touchmove', handleScroll, { passive: true });
}

function removeAutoHideListeners() {
  window.removeEventListener('scroll', handleScroll);
  window.removeEventListener('wheel', handleWheel);
  window.removeEventListener('touchmove', handleScroll);
}

function applyAutoHideMode() {
  removeAutoHideListeners();
  clearTimeout(hideTimer);
  if (scrollRaf) {
    cancelAnimationFrame(scrollRaf);
    scrollRaf = null;
  }

  if (!autoHideQuery.matches) {
    // Desktop: reveal and leave it alone. Matters when crossing the breakpoint
    // while hidden — otherwise it would stay invisible with no way back.
    isHidden.value = false;
    return;
  }

  addAutoHideListeners();
  scheduleHide(INTRO_HOLD);
}

// Dock-style magnifier (macOS Dock algorithm): as the cursor moves along the
// nav, every pill scales based on its distance from the cursor — not just
// the one directly hovered — the same "distance -> [1, MAX_SCALE, 1]"
// interpolation macOS uses horizontally, applied vertically here. The pill's
// transform-origin is anchored to its left (wall) edge, so growth pushes it
// away from the wall rather than overflowing off-screen — the same trick the
// reference dock uses with `origin-bottom` to lift icons off the dock floor.
//
// Spacing is kept collision-free by growing each item's own margin (a real
// layout property) instead of hand-computing a neighbor "shift" — the flex
// list's own `gap` already guarantees a baseline minimum, and the layout
// engine reflows every sibling automatically as margins grow, so we never
// have to reason about cumulative overlap ourselves.
const navList = ref(null);
const MAX_SCALE = 1.4;
const RADIUS = 90; // px of vertical influence around the cursor
const SPRING = { duration: 0.45, ease: 'back.out(1.8)' };
let entries = [];
let resizeRaf = null;

// Base (neutral) positions are derived analytically from the first item's
// rect plus a measured index spacing, rather than re-reading each item's
// live rect — since margin now changes real layout, a live re-read would
// reflect the *current* (already-adjusted) position and feed back into the
// next mousemove's distance calculation, drifting over time.
function measureItems() {
  if (!entries.length) return;

  const first = entries[0].itemEl.getBoundingClientRect();
  const spacing = entries.length > 1
    ? entries[1].itemEl.getBoundingClientRect().top - first.top
    : first.height;
  const firstCenterY = first.top + first.height / 2;

  entries.forEach((entry, i) => {
    entry.baseHeight = first.height;
    entry.baseCenterY = firstCenterY + i * spacing;
  });
}

function updateMagnifier(event) {
  const cursorY = event.clientY;

  entries.forEach((entry) => {
    const distance = cursorY - entry.baseCenterY;
    const absDistance = Math.abs(distance);
    const influence = absDistance > RADIUS ? 0 : 1 - absDistance / RADIUS;
    const scale = 1 + influence * (MAX_SCALE - 1);

    // Half of how much taller the pill visually gets — reserved as margin
    // above and below so it never paints into a neighbor's space. The
    // list's base `gap` (12px) is untouched and still applies on top of
    // this, so the minimum gap only ever grows, never shrinks.
    const extraMargin = (entry.baseHeight / 2) * (scale - 1);

    gsap.to(entry.pillEl, { scale, ...SPRING, overwrite: 'auto' });
    gsap.to(entry.itemEl, { marginTop: extraMargin, marginBottom: extraMargin, ...SPRING, overwrite: 'auto' });
  });
}

function resetMagnifier() {
  entries.forEach((entry) => {
    gsap.to(entry.pillEl, { scale: 1, ...SPRING, overwrite: 'auto' });
    gsap.to(entry.itemEl, { marginTop: 0, marginBottom: 0, ...SPRING, overwrite: 'auto' });
  });
}

// Same guard as the scroll track, for the same reason: the address bar sliding
// away fires resize repeatedly, and this tweens every pill and then re-measures
// them all. None of that is wanted for a height change, and the magnifier this
// serves is a pointer feature that a phone never uses anyway.
let lastNavWidth = typeof window === 'undefined' ? 0 : window.innerWidth;

function handleResize() {
  if (window.innerWidth === lastNavWidth) return;
  lastNavWidth = window.innerWidth;

  if (resizeRaf) cancelAnimationFrame(resizeRaf);
  resizeRaf = requestAnimationFrame(() => {
    resetMagnifier();
    requestAnimationFrame(measureItems);
  });
}


onMounted(() => {
  const items = navList.value ? Array.from(navList.value.querySelectorAll('.side-nav__item')) : [];
  entries = items.map((itemEl) => ({
    itemEl,
    pillEl: itemEl.querySelector('.side-nav__pill'),
    baseHeight: 0,
    baseCenterY: 0,
  }));
  measureItems();

  navList.value?.addEventListener('mousemove', updateMagnifier);
  navList.value?.addEventListener('mouseleave', resetMagnifier);
  window.addEventListener('resize', handleResize);

  // Matches the layout breakpoint where the page stops scrolling horizontally
  // and the panels stack.
  autoHideQuery = window.matchMedia('(max-width: 768px)');
  // Whether hovering means anything on this device. A touch screen reports no
  // hover, and its :hover state is a leftover from the last tap rather than
  // somebody's finger resting on the dock.
  hoverQuery = window.matchMedia('(hover: hover)');
  autoHideQuery.addEventListener('change', applyAutoHideMode);
  applyAutoHideMode();
});

onUnmounted(() => {
  navList.value?.removeEventListener('mousemove', updateMagnifier);
  navList.value?.removeEventListener('mouseleave', resetMagnifier);
  window.removeEventListener('resize', handleResize);
  if (resizeRaf) cancelAnimationFrame(resizeRaf);

  removeAutoHideListeners();
  autoHideQuery?.removeEventListener('change', applyAutoHideMode);
  clearTimeout(hideTimer);
  if (scrollRaf) cancelAnimationFrame(scrollRaf);
});
</script>

<style lang="scss" scoped>
.side-nav {
  position: fixed;
  left: clamp(0.75rem, 2vw, 1.5rem);
  top: 50%;
  transform: translateY(-50%);
  z-index: 2000;
  background: transparent;
  border: none;
  transition: opacity 0.35s ease, transform 0.35s ease;

  // Idle state. Kept in the layout (not display:none) so the magnifier's
  // measured base positions survive, and left focusable so tabbing to it
  // still works — focus reveals it again.
  &.is-hidden {
    opacity: 0;
    transform: translateY(-50%) translateX(-12px);
    pointer-events: none;
  }

  * {
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
  }
}

.side-nav__list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  list-style: none;
  margin: 0;
  padding: 0;
  background: transparent;
  border: none;
}

.side-nav__item {
  position: relative;
}

.side-nav__pill {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  width: 44px;
  padding: 0;
  border-radius: 9999px;
  // Deliberately NOT backdrop-filter: measured ~25% of frame budget on its
  // own, since this nav is position:fixed over content that moves on every
  // scroll frame, forcing the browser to re-read and re-blur its backdrop
  // continuously. A slightly more opaque flat fill reads almost identically.
  background: rgba(var(--background-rgb), 0.82);
  border: 1px solid rgba(var(--accent-rgb), 0.15);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  color: var(--text);
  text-decoration: none;
  overflow: hidden;
  // Anchored to its own left edge (the wall side) — so the magnifier's scale
  // grows the pill rightward, away from the wall, instead of overflowing off
  // screen. This is the same trick the reference dock does with
  // `origin-bottom` to lift icons off the dock floor as they scale.
  transform-origin: left center;
  transition: background 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease, color 0.3s ease;

  &:hover,
  &:focus-visible {
    background: rgba(var(--background-rgb), 0.9);
    border-color: rgba(var(--accent-rgb), 0.5);
    box-shadow: 0 8px 24px rgba(var(--accent-rgb), 0.2);
    color: var(--accent);
    text-decoration: none;
  }

  // Current section: filled rather than merely tinted, so it stays legible
  // against whatever panel happens to be behind the nav.
  &.is-active {
    background: var(--accent);
    border-color: var(--accent);
    color: #fff;
  }
}

.side-nav__icon {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
}

// Floating label — a sibling of the pill (not nested inside it), so it never
// inherits the pill's own magnifier scale and stays normal-sized, like a
// tooltip. Revealed only on a direct hover/focus of that pill.
.side-nav__label {
  position: absolute;
  left: 100%;
  top: 50%;
  margin-left: 0.75rem;
  transform: translateY(-50%) translateX(-4px);
  padding: 0.4rem 0.75rem;
  border-radius: 8px;
  background: rgba(var(--background-rgb), 0.96);
  border: 1px solid rgba(var(--accent-rgb), 0.2);
  font-family: 'Orbitron', sans-serif;
  font-size: 0.7rem;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: var(--text);
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.side-nav__pill:hover + .side-nav__label,
.side-nav__pill:focus-visible + .side-nav__label {
  opacity: 1;
  transform: translateY(-50%) translateX(0);
}

// Fade only — the slide is decoration, the disappearance is the message.
@media (prefers-reduced-motion: reduce) {
  .side-nav {
    transition: opacity 0.2s ease;

    &.is-hidden {
      transform: translateY(-50%);
    }
  }
}

@media (max-width: 768px) {
  .side-nav {
    left: 0.5rem;
  }

  // No labels on a phone. They are revealed by :hover, which sticks to whatever
  // was tapped last on a touch screen, so a label stayed up over the content
  // after every tap.
  .side-nav__label {
    display: none;
  }

  .side-nav__pill {
    height: 38px;
    width: 38px;
  }

  .side-nav__icon {
    width: 38px;
    height: 38px;

    svg {
      width: 17px;
      height: 17px;
    }
  }
}
</style>
